import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/AppContext';
import Sidebar from '../components/Sidebar';
import LogoutModal from '../components/LogoutModal';
import Notification from '../components/Notification';
import axios from 'axios';
import './Dashboard.css';

const NotifPopup = ({ onClose }) => {
  const ref = useRef(null);
  const tr = useT();
  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(() => {
    const stored = JSON.parse(localStorage.getItem('appNotifications') || '[]');
    return stored.filter(n => !n.read).length;
  });

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('appNotifications') || '[]');
    setNotifs(stored);
  }, []); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const markRead = (id) => {
    const updated = notifs.map(n => n.id === id ? { ...n, read: true } : n);
    setNotifs(updated);
    setUnreadCount(updated.filter(n => !n.read).length);
    localStorage.setItem('appNotifications', JSON.stringify(updated));
  };

  const markAllRead = () => {
    const updated = notifs.map(n => ({ ...n, read: true }));
    setNotifs(updated);
    setUnreadCount(0);
    localStorage.setItem('appNotifications', JSON.stringify(updated));
  };

  const deleteNotif = (id) => {
    const updated = notifs.filter(n => n.id !== id);
    setNotifs(updated);
    setUnreadCount(updated.filter(n => !n.read).length);
    localStorage.setItem('appNotifications', JSON.stringify(updated));
  };

  const clearAll = () => {
    setNotifs([]);
    setUnreadCount(0);
    localStorage.removeItem('appNotifications');
  };

  const iconColor = { success: '#22c55e', info: 'var(--accent-start)', warning: '#f59e0b' };

  return (
    <div className="notif-popup" ref={ref}>
      <div className="notif-popup-header">
        <span className="notif-popup-title">
          {tr('notifications')} {unreadCount > 0 && <span className="notif-count">{unreadCount}</span>}
        </span>
        <button className="notif-mark-all-btn" onClick={markAllRead} title="Tout marquer comme lu">
          <i className="fas fa-check-double"></i>
        </button>
      </div>
      <div className="notif-popup-list">
        {notifs.length === 0 ? (
          <div className="notif-empty">
            <i className="fas fa-bell-slash"></i>
            <p>{tr('aucune_notification')}</p>
          </div>
        ) : notifs.map(n => (
          <div
            key={n.id}
            className={`notif-item${n.read ? '' : ' notif-item-unread'}`}
            onClick={() => markRead(n.id)}
          >
            <div className="notif-icon" style={{ background: iconColor[n.type] + '20', color: iconColor[n.type] }}>
              <i className={`fas ${n.icon}`}></i>
            </div>
            <div className="notif-text">
              <div className="notif-item-title">
                {n.title} {!n.read && <span className="notif-dot"></span>}
              </div>
              <p className="notif-item-desc">{n.desc}</p>
              <span className="notif-item-time">{n.time}</span>
            </div>
            <button className="notif-delete" onClick={e => { e.stopPropagation(); deleteNotif(n.id); }}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        ))}
      </div>
      <div className="notif-popup-footer">
        <button className="notif-clear-btn" onClick={clearAll}>
          <i className="fas fa-trash-alt"></i> {tr('tout_effacer')}
        </button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const tr = useT();
  const [stats, setStats] = useState({ invoices: 0, cvs: 0, downloads: 0 });
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const res = await axios.get('/pdf/stats');
      setStats(res.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  return (
    <div className="page-with-sidebar">
      <Sidebar />
      {notification && (
        <Notification type={notification.type} message={notification.message} onClose={() => setNotification(null)} />
      )}

      <main className="dashboard-main">
        <div className="top-bar-modern">
          <div className="top-bar-left">
            <h1 className="page-title-modern">{tr('dashboard')}</h1>
          </div>
          <div className="top-bar-right">
            <button className="btn-wallet-modern">
              <i className="fas fa-wallet"></i>
              <span className="wallet-label"> {tr('compte_connecte')}</span>
            </button>
            <div className="notif-bell-wrapper">
              <button className="btn-icon-modern" onClick={() => setShowNotifications(v => !v)}>
                <i className="fas fa-bell"></i>
              </button>
              {showNotifications && <NotifPopup onClose={() => setShowNotifications(false)} />}
            </div>
            <button className="btn-icon-modern" onClick={() => setShowLogoutModal(true)}>
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </div>

        <div className="welcome-row">
          <div className="welcome-content">
            <h2>{tr('bienvenue')}, {user?.name}</h2>
            <p className="subtitle-modern">
              <i className="fas fa-info-circle"></i> {tr('creer_document')}
            </p>
          </div>
          <div className="earnings-card-modern">
            <div className="earnings-header">
              <i className="fas fa-chart-line"></i><span>{tr('docs_par_jour')}</span>
            </div>
            <h3 className="earnings-amount">{stats.downloads}</h3>
          </div>
        </div>

        <div className="dashboard-grid-full">
          <div className="create-document-card">
            <div className="wallet-icon-large"><i className="fas fa-file-pdf"></i></div>
            <h3>{tr('creer_document')}</h3>
            <p>{tr('choisir_type')}</p>
            <div className="document-buttons">
              <button className="btn-create-doc" onClick={() => navigate('/invoice')}>
                <i className="fas fa-file-invoice"></i> {tr('facture')}
              </button>
              <button className="btn-create-doc" onClick={() => navigate('/cv')}>
                <i className="fas fa-file-alt"></i> CV
              </button>
            </div>
            <div className="stats-mini">
              <div className="stat-mini-item">
                <span className="stat-label">{tr('factures_creees')}</span>
                <span className="stat-value">{stats.invoices}</span>
              </div>
              <div className="stat-mini-item">
                <span className="stat-label">{tr('cvs_generes')}</span>
                <span className="stat-value">{stats.cvs}</span>
              </div>
              <div className="stat-mini-item">
                <span className="stat-label">{tr('total_telechargements')}</span>
                <span className="stat-value">{stats.downloads}</span>
              </div>
            </div>
          </div>

          <div className="stats-card-dark">
            <div className="stats-header">
              <div className="stats-title">
                <span>{tr('productivite')}</span>
                <button className="btn-expand"><i className="fas fa-expand-alt"></i></button>
              </div>
            </div>
            <div className="big-stat">
              <h2 className="percentage-large">
                {stats.downloads > 0 ? ((stats.downloads / 100) * 100).toFixed(0) : '0'}%
              </h2>
              <span className="stat-label-white">{tr('taux_utilisation')}</span>
            </div>
            <div className="total-value-section">
              <div className="total-label">
                <span>{tr('docs_totaux')}</span>
                <i className="fas fa-arrow-trend-up"></i>
              </div>
              <h3 className="total-amount">{stats.invoices + stats.cvs}</h3>
            </div>
            <div className="current-index-section">
              <div className="index-label">
                <span>{tr('tendance_mensuelle')}</span>
                <i className="fas fa-chart-line"></i>
              </div>
              <h4 className="index-value">
                {stats.downloads > 0 ? (stats.downloads * 1.5).toFixed(0) : '0'} <span className="index-unit">DOCS</span>
              </h4>
            </div>
          </div>
        </div>

        <div className="quick-actions-section">
          <h3 className="section-title-modern">{tr('actions_rapides')}</h3>
          <div className="quick-actions-grid-full">
            <div className="action-card" onClick={() => navigate('/invoice')}>
              <div className="action-icon invoice"><i className="fas fa-file-invoice"></i></div>
              <h4>{tr('nouvelle_facture')}</h4><p>{tr('facture_pro')}</p>
              <div className="action-arrow"><i className="fas fa-arrow-right"></i></div>
            </div>
            <div className="action-card" onClick={() => navigate('/cv')}>
              <div className="action-icon cv"><i className="fas fa-file-alt"></i></div>
              <h4>{tr('nouveau_cv')}</h4><p>{tr('cv_ats')}</p>
              <div className="action-arrow"><i className="fas fa-arrow-right"></i></div>
            </div>
            <div className="action-card" onClick={() => navigate('/parametres')}>
              <div className="action-icon settings"><i className="fas fa-cog"></i></div>
              <h4>{tr('parametres_nav')}</h4><p>{tr('configurer_compte')}</p>
              <div className="action-arrow"><i className="fas fa-arrow-right"></i></div>
            </div>
            <div className="action-card" onClick={() => navigate('/documentation')}>
              <div className="action-icon docs"><i className="fas fa-book"></i></div>
              <h4>{tr('documentation')}</h4><p>{tr('guide')}</p>
              <div className="action-arrow"><i className="fas fa-arrow-right"></i></div>
            </div>
          </div>
        </div>

        <div className="dashboard-footer-modern">
          <div className="footer-left">
            <span>&copy; 2024 PDF Generator Pro.</span>
          </div>
          <div className="footer-right">
            <div className="verification-badge"><i className="fas fa-check-circle"></i><span>KYC</span></div>
            <div className="verification-badge"><i className="fas fa-shield-alt"></i><span>SSL</span></div>
          </div>
        </div>
      </main>

      <LogoutModal isOpen={showLogoutModal} onClose={() => setShowLogoutModal(false)} onConfirm={() => { setShowLogoutModal(false); logout(); navigate('/'); }} />
    </div>
  );
};

export default Dashboard;